import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { qrApi, vehicleMileageApi, formatApiError } from '../services/api';
import { resolveStorageUrl } from '../services/storage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import BlockDialog from '../components/BlockDialog';
import {
  ArrowLeft, Car, SignIn, SignOut, Warning, Wrench, Speedometer, IdentificationCard,
  CheckCircle, XCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  available: 'Verfügbar', rented: 'Vermietet',
  maintenance: 'Wartung', blocked: 'Blockiert',
};
const STATUS_STYLE = {
  available: 'bg-[#009951] text-white',
  rented: 'bg-[#E60000] text-white',
  maintenance: 'bg-[#FFB800] text-[#111418]',
  blocked: 'bg-[#525866] text-white',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch { return iso; }
};

const QuickAction = ({ icon: Icon, label, sub, onClick, color = '#E60000', testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full bg-white border border-[#E2E4E9] rounded-sm p-4 flex items-center gap-3 hover:border-[#111418] hover:shadow-sm transition-all text-left"
    data-testid={testid}
  >
    <span className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: color + '15' }}>
      <Icon size={24} weight="fill" style={{ color }} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-semibold text-[#111418]">{label}</span>
      {sub && <span className="block text-xs text-[#525866] truncate">{sub}</span>}
    </span>
  </button>
);

const ScanPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showMileage, setShowMileage] = useState(false);
  const [mileage, setMileage] = useState('');
  const [savingMileage, setSavingMileage] = useState(false);
  const [showBlock, setShowBlock] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setErr(null);
    qrApi.resolve(token)
      .then((r) => {
        if (!active) return;
        // BOOK:<id> — direkt zur Buchung springen
        if (r.data?.type === 'booking' && r.data?.booking?.id) {
          toast.success(`Buchung ${r.data.booking.booking_number || ''} geöffnet`);
          navigate(`/bookings/${r.data.booking.id}`, { replace: true });
          return;
        }
        setData(r.data);
        setMileage(r.data?.vehicle?.current_mileage ?? '');
      })
      .catch((e) => { if (active) setErr(formatApiError(e) || 'Fahrzeug nicht gefunden'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token, navigate]);

  const saveMileage = async () => {
    if (mileage === '' || isNaN(parseInt(mileage))) {
      toast.error('Bitte Kilometerstand eingeben'); return;
    }
    try {
      setSavingMileage(true);
      const r = await vehicleMileageApi.update(data.vehicle.id, parseInt(mileage));
      setData((prev) => ({ ...prev, vehicle: { ...prev.vehicle, current_mileage: r.data.current_mileage } }));
      toast.success('Kilometerstand aktualisiert');
      setShowMileage(false);
    } catch (e) {
      toast.error(formatApiError(e) || 'Speichern fehlgeschlagen');
    } finally {
      setSavingMileage(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFB]"><p className="text-sm text-[#525866]">Lade Fahrzeug…</p></div>;
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFB] p-4">
        <div className="card-swiss p-6 max-w-sm w-full text-center">
          <XCircle size={48} className="mx-auto text-[#E60000] mb-3" weight="fill" />
          <h1 className="text-lg font-bold mb-2">Code nicht erkannt</h1>
          <p className="text-sm text-[#525866] mb-4">{err}</p>
          <Button onClick={() => navigate('/dashboard')} className="btn-primary w-full">Zurück zum Dashboard</Button>
        </div>
      </div>
    );
  }

  const veh = data.vehicle;
  const active = data.active_booking;
  const upcoming = data.upcoming_booking;

  return (
    <div className="min-h-screen bg-[#FAFAFB]" data-testid="scan-page">
      <div className="bg-[#111418] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="hover:opacity-70" aria-label="Zurück">
          <ArrowLeft size={20} weight="bold" />
        </button>
        <div className="flex-1">
          <p className="text-xs text-white/60 uppercase tracking-wide">Scan</p>
          <p className="text-sm font-semibold">{veh.brand} {veh.model}</p>
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded-sm uppercase ${STATUS_STYLE[veh.status] || 'bg-[#525866] text-white'}`}>
          {STATUS_LABEL[veh.status] || veh.status}
        </span>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Vehicle summary card */}
        <div className="card-swiss p-4 flex gap-3">
          {veh.image_url ? (
            <img src={resolveStorageUrl(veh.image_url)} alt={veh.license_plate} className="w-20 h-20 object-cover rounded-sm flex-shrink-0" />
          ) : (
            <div className="w-20 h-20 bg-[#F1F2F4] rounded-sm flex items-center justify-center flex-shrink-0">
              <Car size={32} className="text-[#8A94A6]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-mono text-lg font-bold tracking-wide">{veh.license_plate}</p>
            <p className="text-sm text-[#525866]">{veh.brand} {veh.model} · {veh.year}</p>
            <p className="text-xs text-[#8A94A6] mt-1">
              {veh.current_mileage != null
                ? `${Number(veh.current_mileage).toLocaleString('de-DE')} km`
                : 'Kein km-Stand erfasst'}
            </p>
          </div>
        </div>

        {/* Active / Upcoming booking banner */}
        {active && (
          <div className="bg-[#E60000]/10 border border-[#E60000]/30 rounded-sm p-3">
            <p className="text-xs uppercase tracking-wide text-[#E60000] font-bold mb-1">Aktive Vermietung</p>
            <p className="text-sm font-semibold">{active.customer?.first_name || ''} {active.customer?.last_name || ''}</p>
            <p className="text-xs text-[#525866]">{active.booking_number} · bis {formatDateTime(active.end_date)}</p>
          </div>
        )}
        {!active && upcoming && (
          <div className="bg-[#FFB800]/15 border border-[#FFB800]/40 rounded-sm p-3">
            <p className="text-xs uppercase tracking-wide text-[#B36200] font-bold mb-1">Nächste Buchung</p>
            <p className="text-sm font-semibold">{upcoming.customer?.first_name || ''} {upcoming.customer?.last_name || ''}</p>
            <p className="text-xs text-[#525866]">{upcoming.booking_number} · ab {formatDateTime(upcoming.start_date)}</p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="space-y-2">
          <p className="overline text-[#8A94A6]">Schnell-Aktionen</p>

          <QuickAction
            icon={IdentificationCard}
            label="Fahrzeug-Info"
            sub="Stammdaten, Buchungen, Schäden"
            onClick={() => navigate(`/vehicles/${veh.id}`)}
            color="#111418"
            testid="scan-action-info"
          />

          {active && (
            <QuickAction
              icon={SignOut}
              label="Rückgabe (Check-Out)"
              sub={`Buchung ${active.booking_number}`}
              onClick={() => navigate(`/bookings/${active.id}?action=checkout`)}
              color="#E60000"
              testid="scan-action-checkout"
            />
          )}

          {!active && upcoming && (
            <QuickAction
              icon={SignIn}
              label="Übergabe (Check-In)"
              sub={`Buchung ${upcoming.booking_number}`}
              onClick={() => navigate(`/bookings/${upcoming.id}?action=checkin`)}
              color="#009951"
              testid="scan-action-checkin"
            />
          )}

          <QuickAction
            icon={Warning}
            label="Schaden erfassen"
            sub="Neuen Schaden anlegen"
            onClick={() => navigate(`/damages?vehicle_id=${veh.id}&new=1`)}
            color="#FFB800"
            testid="scan-action-damage"
          />

          <QuickAction
            icon={Wrench}
            label="Werkstatt eintragen"
            sub="Interne Belegung anlegen"
            onClick={() => setShowBlock(true)}
            color="#525866"
            testid="scan-action-workshop"
          />

          <QuickAction
            icon={Speedometer}
            label="Kilometerstand aktualisieren"
            sub={veh.current_mileage != null ? `Aktuell: ${Number(veh.current_mileage).toLocaleString('de-DE')} km` : 'Noch nicht gesetzt'}
            onClick={() => setShowMileage(true)}
            color="#2E62FF"
            testid="scan-action-mileage"
          />
        </div>
      </div>

      {/* Mileage modal */}
      <Dialog open={showMileage} onOpenChange={setShowMileage}>
        <DialogContent className="max-w-sm" data-testid="scan-mileage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Speedometer size={18} weight="fill" className="text-[#2E62FF]" />
              Kilometerstand
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#525866]">{veh.brand} {veh.model} — {veh.license_plate}</p>
            <div>
              <Label className="overline">Neuer Kilometerstand</Label>
              <Input
                type="number"
                value={mileage}
                onChange={(e) => setMileage(e.target.value)}
                min={0}
                className="input-swiss text-xl tabular-nums text-center"
                data-testid="scan-mileage-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowMileage(false)}>Abbrechen</Button>
            <Button type="button" onClick={saveMileage} disabled={savingMileage} className="btn-primary" data-testid="scan-mileage-save-btn">
              <CheckCircle size={14} className="mr-1" />
              {savingMileage ? 'Speichere…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BlockDialog with vehicle pre-filled */}
      <BlockDialog
        open={showBlock}
        onOpenChange={setShowBlock}
        prefill={{ vehicle_id: veh.id }}
        onSaved={() => { toast.success('Belegung angelegt'); setShowBlock(false); }}
      />
    </div>
  );
};

export default ScanPage;
